import { motion } from "framer-motion";
import { Database, FileText } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const rise = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, ease: EASE, delay },
});

const card =
    "group relative overflow-hidden rounded-3xl border border-[#0F172A]/[0.07] bg-white p-8 shadow-[0_2px_12px_-4px_rgba(11,18,32,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_45px_-20px_rgba(11,18,32,0.18)] sm:p-10";

const Label = ({ children }) => (
    <p className="text-sm font-bold uppercase tracking-[0.12em] text-[#0F172A]">{children}</p>
);

const Body = ({ children }) => (
    <p className="mt-3 max-w-xs text-[15px] leading-relaxed text-[#64748B]">{children}</p>
);

const Waves = () => (
    <svg aria-hidden="true" viewBox="0 0 200 90" className="absolute -right-4 top-6 w-44 opacity-60">
        {[0, 1, 2].map((i) => (
            <path
                key={i}
                d={`M0 ${30 + i * 16} C 40 ${14 + i * 16}, 60 ${46 + i * 16}, 100 ${30 + i * 16} S 160 ${14 + i * 16}, 200 ${30 + i * 16}`}
                fill="none"
                stroke={i === 1 ? "#A855F7" : "#0066FF"}
                strokeOpacity={0.25 - i * 0.06}
                strokeWidth="1.2"
            />
        ))}
        <circle cx="100" cy="46" r="3" fill="#0066FF" fillOpacity="0.5" />
    </svg>
);

const FlowArrows = () => (
    <svg aria-hidden="true" viewBox="0 0 220 100" className="absolute -right-2 top-1/2 w-52 -translate-y-1/2 opacity-60">
        {[0, 1, 2].map((i) => (
            <g key={i}>
                <path
                    d={`M0 ${18 + i * 32} C 80 ${18 + i * 32}, 120 ${18 + i * 32}, 196 ${18 + i * 32}`}
                    fill="none"
                    stroke={i === 1 ? "#D946EF" : "#0066FF"}
                    strokeOpacity={0.22}
                    strokeWidth="1.2"
                />
                <path
                    d={`M190 ${13 + i * 32} l 8 5 -8 5`}
                    fill="none"
                    stroke={i === 1 ? "#D946EF" : "#0066FF"}
                    strokeOpacity="0.35"
                    strokeWidth="1.2"
                />
            </g>
        ))}
    </svg>
);

const LayeredSquares = ({ className }) => (
    <div aria-hidden="true" className={className}>
        {[0, 1, 2].map((i) => (
            <div
                key={i}
                className="absolute rounded-2xl border"
                style={{
                    inset: `${i * 14}px`,
                    borderColor: ["#00E5FF", "#0066FF", "#A855F7"][i],
                    opacity: 0.22 - i * 0.05,
                    transform: `rotate(${-10 + i * 6}deg)`,
                }}
            />
        ))}
    </div>
);

export default function Impact() {
    return (
        <section
            className="relative bg-white"
            style={{
                backgroundImage:
                    "linear-gradient(to right, rgba(15,23,42,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.035) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
            }}
            data-testid="impact-section"
            aria-labelledby="impact-heading"
        >
            <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
                <div className="grid gap-6 lg:grid-cols-3">
                    <motion.div {...rise(0)} className={`${card} flex flex-col justify-center lg:row-span-2`} data-testid="impact-card-1">
                        <LayeredSquares className="absolute -right-10 bottom-10 h-52 w-52" />
                        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[#0F172A]/10 bg-white px-4 py-1.5 shadow-sm" data-testid="impact-eyebrow">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#0066FF]" />
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#475569] sm:text-xs">
                                The TedOps Impact
                            </span>
                        </span>
                        <h2
                            id="impact-heading"
                            className="mt-8 font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-[#0F172A] sm:text-5xl"
                            data-testid="impact-heading"
                        >
                            Intelligent
                            <br />
                            Operations.
                            <br />
                            <span className="text-brand-gradient">Measurable Results.</span>
                        </h2>
                    </motion.div>

                    <motion.div {...rise(0.1)} className={card} data-testid="impact-card-2">
                        <LayeredSquares className="absolute -right-8 -top-10 h-36 w-36" />
                        <p className="font-display text-5xl font-extrabold tracking-tight text-[#0F172A] sm:text-6xl">1</p>
                        <div className="mt-8">
                            <Label>Connected Platform</Label>
                            <Body>One place for enquiries, scheduling, field jobs, stock and invoicing — with AI working quietly across all of it.</Body>
                        </div>
                    </motion.div>

                    <motion.div {...rise(0.18)} className={card} data-testid="impact-card-3">
                        <Waves />
                        <p className="font-display text-5xl font-extrabold tracking-tight text-[#0F172A] sm:text-6xl">LIVE</p>
                        <div className="mt-8">
                            <Label>Operational Visibility</Label>
                            <Body>See jobs, teams and progress in real time, with AI flagging what needs attention first.</Body>
                        </div>
                    </motion.div>

                    <motion.div {...rise(0.26)} className={card} data-testid="impact-card-4">
                        <div className="flex items-center justify-between gap-4">
                            <p className="font-display text-5xl font-extrabold tracking-tight text-[#0F172A] sm:text-6xl">0</p>
                            <div aria-hidden="true" className="flex items-center gap-3 opacity-70">
                                <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#0066FF]/20 bg-white shadow-sm">
                                    <FileText className="h-4 w-4 text-[#0066FF]" strokeWidth={1.75} />
                                </span>
                                <span className="h-px w-6 border-t border-dashed border-[#64748B]/50" />
                                <span className="h-2.5 w-2.5 rounded-full bg-gradient-to-br from-[#0066FF] to-[#A855F7]" />
                                <span className="h-px w-6 border-t border-dashed border-[#64748B]/50" />
                                <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#A855F7]/20 bg-white shadow-sm">
                                    <Database className="h-4 w-4 text-[#A855F7]" strokeWidth={1.75} />
                                </span>
                            </div>
                        </div>
                        <div className="mt-8">
                            <Label>Double Entry</Label>
                            <Body>AI moves data between office, field and accounts, so nothing gets rekeyed.</Body>
                        </div>
                    </motion.div>

                    <motion.div {...rise(0.34)} className={card} data-testid="impact-card-5">
                        <FlowArrows />
                        <p className="font-display text-5xl font-extrabold tracking-tight text-[#0F172A] sm:text-6xl">FASTER</p>
                        <div className="mt-8">
                            <Label>Billing Workflow</Label>
                            <Body>Completed work, forms and photos flow into AI-checked invoicing with less admin.</Body>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
